// 신호 예약 — 프로파일의 값을 오디오 시계 위에 건다
//
// 이 파일은 **값을 갖지 않는다.** 표지가 몇 개이고 어느 음고인지는 profile.ts 가 갖고,
// 여기는 그 값을 읽어 예약만 한다. 갈라 둔 목적은 하나다 — 다음에 귀로 듣고 소리를
// 고칠 때 흔들리는 파일이 하나뿐이게 하는 것.
//
// ── 되돌리면 안 되는 규율 셋 ──────────────────────────────────────────────
//
// 하나 — **오디오 시계에 미리 예약한다.** setTimeout 으로 소리를 내면 그 순간의 이벤트
// 루프 사정이 그대로 개시 시각의 오차가 된다. 이 제품은 1~2초를 다투는 도구라 그 오차가
// 실제 실패다. OscillatorNode 의 start(t)·stop(t) 와 AudioParam 자동화는 오디오 스레드가
// 샘플 단위로 지키므로 자바스크립트가 잠깐 밀려도 소리는 제자리에 떨어진다.
//
// 둘 — **오디오 워크릿을 쓰지 않는다.** 워크릿 모듈은 별도 스크립트를 불러오는데 그
// 경로가 도메인 없는 HTTP 에서 막힌다. 이 제품의 배포 조건이 정확히 그것이라 워크릿에
// 기대면 로컬에서만 되는 신호가 된다.
//
// 셋 — **늦었으면 울리지 않는다.** 늦은 신호는 침묵보다 나쁘다. 사람이 그 소리를
// 개시로 믿고 실제보다 늦게 rally 를 열기 때문이다.
//
// **셋째 규율(늦었으면 울리지 않는다)을 예약 시점에 한 번만 검사하면 모자란다.** 기기가
// 백그라운드로 가면 오디오 시계가 멈추고, 예약된 소리는 취소되는 것이 아니라 통째로
// 늦게 울린다. 격자 형태에서는 표지 다섯과 출발음이 **오디오 시계 위에서는 등간격을
// 완벽히 유지한 채** 늦으므로, 흠 없는 다섯 박을 들은 사람이 **틀린 시각을 확신하고**
// 누른다. 그래서 예약을 건 뒤에도 오디오 시계와 서버 시각을 다시 대조해, 어긋나면 걸린
// 예약을 걷고 다시 건다(아래 `decideSignalRefresh`). 그것을 매 틱 부르는 쪽은 공격 열람
// 스텝(T6)이 만든 `runtime.ts` 다. 이 경로를 공격 열람 스텝으로 미룬 기록은 구현 계획서
// 「T6. 공격 열람」 스텝의 늦은 신호 재예약 항목에 있다.

import { SIGNAL_PROFILE, type SignalProfile, type SignalRole } from './profile.ts'

/** 취소할 때 마스터 버스를 0으로 내리는 데 쓰는 시간. 딸깍 소리를 막을 만큼만 짧다. */
export const CANCEL_RAMP_SEC = 0.005

// 브라우저 타입에 직접 기대지 않고 필요한 만큼만 적는다. 이유가 둘이다. Node 안의 단위
// 테스트가 가짜 컨텍스트를 세워 **예약된 시각 자체**를 잴 수 있고, 같은 인터페이스를
// OfflineAudioContext 도 만족하므로 **기계 판정이 실제 예약 코드를 그대로 렌더**할 수
// 있다. 판정용으로 따로 만든 코드를 판정하면 아무것도 검증되지 않는다.

export interface AudioParamLike {
  value: number
  setValueAtTime(value: number, at: number): void
  linearRampToValueAtTime(value: number, at: number): void
  cancelScheduledValues(at: number): void
}

export interface OscillatorLike {
  type: string
  frequency: { value: number }
  connect(destination: unknown): void
  start(when: number): void
  stop(when: number): void
}

export interface GainLike {
  gain: AudioParamLike
  connect(destination: unknown): void
}

export interface AudioContextLike {
  readonly currentTime: number
  readonly state: string
  readonly destination: unknown
  createOscillator(): OscillatorLike
  createGain(): GainLike
  resume(): Promise<void>
}

export interface ScheduledEvent {
  readonly role: SignalRole
  /** 오디오 시계 위의 절대 온셋. 사람이 소리의 시작으로 듣는 지점이다. */
  readonly onsetSec: number
  /** 오실레이터가 실제로 도는 시각. 램프가 온셋 앞에서 시작하므로 온셋보다 이르다. */
  readonly rampStartSec: number
  readonly frequencyHz: number
  readonly durationSec: number
  readonly releaseSec: number
  readonly peak: number
}

export type SignalPlan =
  | {
      kind: 'scheduled'
      startAtSec: number
      events: ScheduledEvent[]
      /** 이미 지나가 버린 표지의 개수. 화면이 이것을 말해야 사람이 자기 탓을 하지 않는다. */
      droppedMarkers: number
    }
  | { kind: 'too-late'; lateBySec: number }

/**
 * 무엇을 언제 울릴지 계산한다. 오디오 노드를 만들지 않으므로 브라우저 없이도 잰다.
 *
 * 구간 한복판에 들어온 경우 **지나간 표지는 되살리지 않고 버린다.** 지난 박을 지금
 * 울리면 격자가 어긋나고, 어긋난 격자는 없는 격자보다 나쁘다 — 사람이 그것을 믿는다.
 * 종점의 시각은 어느 경우에도 건드리지 않는다.
 */
export function planSignal(input: {
  audioNowSec: number
  startAtSec: number
  profile: SignalProfile
}): SignalPlan {
  const { audioNowSec, startAtSec, profile } = input

  // 같은 시각도 늦은 것으로 본다. 예약할 시간이 0이면 소리는 개시 뒤에 울릴 수밖에
  // 없고, 그 소리는 개시를 알리는 것이 아니라 개시를 놓쳤다는 사실을 숨긴다.
  if (startAtSec <= audioNowSec) {
    return { kind: 'too-late', lateBySec: audioNowSec - startAtSec }
  }

  const half = profile.attackSec / 2
  const events: ScheduledEvent[] = []
  let droppedMarkers = 0

  for (const event of profile.events) {
    const onsetSec = startAtSec + event.onsetSec
    const rampStartSec = onsetSec - half
    if (event.role === 'marker' && rampStartSec <= audioNowSec) {
      droppedMarkers += 1
      continue
    }
    events.push({
      role: event.role,
      onsetSec,
      // 종점은 버리지 않는다. 램프를 온전히 놓을 자리가 없으면 지금부터 시작하되,
      // 진폭이 절반에 닿는 지점은 여전히 개시 근처에 남는다.
      rampStartSec: Math.max(rampStartSec, audioNowSec),
      frequencyHz: event.frequencyHz,
      durationSec: event.durationSec,
      releaseSec: event.releaseSec,
      peak: event.peak,
    })
  }

  return { kind: 'scheduled', startAtSec, events, droppedMarkers }
}

export type SignalResult =
  | {
      kind: 'scheduled'
      startAtSec: number
      events: ScheduledEvent[]
      droppedMarkers: number
      /** 예약 전체를 한 동작으로 되돌린다. */
      cancel: () => void
    }
  | { kind: 'too-late'; lateBySec: number }
  | { kind: 'blocked'; reason: string }

/**
 * 왜 소리가 안 나는지를 사람의 말로 적는다.
 *
 * 도메인 없는 HTTP 에서 소리가 안 나는 경로가 여럿이라 "안 났다" 만으로는 어느 것인지
 * 가릴 수 없다. 무음 스위치인지, 활성화 제스처가 없어서인지, 창이 백그라운드로 밀려
 * 오디오 시계가 멈춘 것인지에 따라 사람이 할 일이 전혀 다르다.
 */
/** 잠김 이유 코드. 화면이 지금 언어의 문장으로 옮긴다. */
export const AUDIO_SUSPENDED = 'audioSuspended'
export const AUDIO_CLOSED = 'audioClosed'
/** 뒤에 오디오 상태 이름이 붙는다(예: `audioNotRunning:interrupted`). */
export const AUDIO_NOT_RUNNING = 'audioNotRunning:'

export function describeBlockedState(state: string): string {
  // **문장이 아니라 이유 코드를 돌려준다** (2026-10-04 T6 개정 둘째, 사용자). 이 층은 언어를 모르는데 예전에는
  // 한국어 문장을 돌려줘서, 영어 화면에서도 잠김 이유가 한국어로 떴다(넷째 판 대리 관측 둘). 화면이 지금
  // 언어로 옮긴다(`signal-zone.tsx` 의 `reasonText`). 문장의 내력은 아래 주석과 i18n 의 `audioSuspended`.
  switch (state) {
    // **화면에 없는 것을 가리키지 않는다.** 예전 문장은 「시험음 버튼을 한 번 더 누르면
    // 풀린다」였는데 그 버튼은 계측 프로브의 것이다. 이 문장이 뜨는 자리가 둘이고
    // (프로브와 연맹원 열람 화면) 열람 화면에 있는 것은 「소리 켜기」라, 폰으로 온 사람은
    // 없는 버튼을 찾다가 자기가 놓쳤다고 여긴다. 두 화면 모두에서 참인 말은 **바로 아래
    // 버튼**이다 — 둘 다 이 안내 바로 밑에 버튼 하나를 두고 있다.
    //
    // 말투도 함께 맞춘다. 이 저장소의 사람 대상 문장은 `~습니다` 이고, 2026-08-14 대리
    // 관측이 이 셋을 「말투가 화면마다 갈린다」의 근거로 적었다.
    case 'suspended':
      return AUDIO_SUSPENDED
    case 'closed':
      return AUDIO_CLOSED
    default:
      return `${AUDIO_NOT_RUNNING}${state}`
  }
}

/**
 * 실제로 예약한다. 성공하면 예약된 시각들을 그대로 돌려주므로, 부른 쪽이 화면에 그
 * 숫자를 적어 사람이 눈으로 대조할 수 있다.
 *
 * **잠긴 오디오를 여기서 막지 않는 이유.** 잠김을 푸는 것은 armAudio 의 몫이고, 그것은
 * 반드시 사람의 제스처 안에서 불러야 한다는 규율을 함께 진다. 두 자리가 같은 판단을
 * 하면 그 규율이 어느 쪽 책임인지 흐려진다. 여기서 막는 것은 **다시 살릴 수 없는
 * 상태**(closed) 하나뿐이고, 그래서 아직 렌더를 시작하지 않아 suspended 인
 * OfflineAudioContext 도 이 함수를 그대로 쓴다 — 기계 판정이 실제 예약 코드를 재는
 * 근거가 그것이다.
 */
export function scheduleSignal(
  ctx: AudioContextLike,
  request: { startAtSec: number; profile?: SignalProfile },
): SignalResult {
  if (ctx.state === 'closed') {
    return { kind: 'blocked', reason: describeBlockedState(ctx.state) }
  }

  const profile = request.profile ?? SIGNAL_PROFILE
  const plan = planSignal({
    audioNowSec: ctx.currentTime,
    startAtSec: request.startAtSec,
    profile,
  })
  if (plan.kind === 'too-late') return plan

  // 마스터 버스를 하나 둔다. 취소가 **한 동작**이어야 하기 때문이다 — 그것을 부르는
  // 자리가 셋이고(신호 대상 전환, retime 재예약, dispatch 후 offset 수정) 노드를 하나씩
  // 걷어 내는 방식은 셋 중 하나만 빠뜨려도 소리가 겹친다. 겹침은 판정 계약의 불통이다.
  const bus = ctx.createGain()
  bus.gain.value = 1
  bus.connect(ctx.destination)

  const half = profile.attackSec / 2
  const oscillators: OscillatorLike[] = []

  for (const event of plan.events) {
    const oscillator = ctx.createOscillator()
    oscillator.type = profile.waveform
    oscillator.frequency.value = event.frequencyHz

    const gain = ctx.createGain()
    gain.gain.value = 0

    const holdEndSec = event.onsetSec + event.durationSec
    const silentSec = holdEndSec + event.releaseSec

    // 온셋 램프는 **목표 시각에 걸쳐** 놓는다. 진폭이 절반에 닿는 지점이 곧 온셋이라,
    // 램프를 목표 뒤에서 시작하면 사람이 듣는 온셋도 기계가 재는 온셋도 통째로 늦는다.
    gain.gain.setValueAtTime(0, event.rampStartSec)
    gain.gain.linearRampToValueAtTime(event.peak, event.onsetSec + half)
    // 오프셋 램프를 둔다. 회수된 형태에서는 끊김이 곧 개시라 램프를 두지 않는 것이
    // 계약이었지만, 새 형태에서 개시는 **어떤 소리의 오프셋도 아니다.** 램프가 없으면
    // 소리가 멎는 자리마다 계단 불연속이 생기고 그것이 여섯 번 반복된다.
    gain.gain.setValueAtTime(event.peak, holdEndSec)
    gain.gain.linearRampToValueAtTime(0, silentSec)

    oscillator.connect(gain)
    gain.connect(bus)
    oscillator.start(event.rampStartSec)
    oscillator.stop(silentSec)
    oscillators.push(oscillator)
  }

  const cancel = (): void => {
    const now = ctx.currentTime
    bus.gain.cancelScheduledValues(now)
    bus.gain.setValueAtTime(bus.gain.value, now)
    bus.gain.linearRampToValueAtTime(0, now + CANCEL_RAMP_SEC)
    // 버스만 내리고 오실레이터를 살려 두면 재예약 때 새 버스로 소리가 나는 동안 옛
    // 오실레이터도 계속 돌아 둘이 겹친다. 취소된 버스는 재사용하지 않는다 — 다음
    // 예약은 이 함수를 다시 불러 새 버스를 만든다.
    for (const oscillator of oscillators) oscillator.stop(now + CANCEL_RAMP_SEC)
  }

  return {
    kind: 'scheduled',
    startAtSec: plan.startAtSec,
    events: plan.events,
    droppedMarkers: plan.droppedMarkers,
    cancel,
  }
}

/** 예약 하나가 무엇을 무엇에 묶었는지. 어긋남을 재려면 이 짝이 남아 있어야 한다. */
export interface SignalBinding {
  /** 오디오 시계 위에 실제로 걸린 개시 시각. */
  readonly startAtSec: number
  /** 그때 겨냥한 서버 시계 위의 개시 시각. */
  readonly targetOpenMs: number
}

export interface SignalDriftVerdict {
  readonly kind: 'on-time' | 'drifted'
  /** 양수면 그만큼 늦게, 음수면 그만큼 이르게 울린다. */
  readonly latenessSec: number
}

/**
 * 이보다 크게 어긋나면 예약을 믿지 않는다. **150밀리초.**
 *
 * 위 한계를 정한 것은 이 제품이 스스로 약속한 표시 편차 예산 p95 200밀리초다. 그보다 더
 * 어긋난 소리는 **바로 위 화면의 숫자와 모순되고**, 그때 사람은 둘 중 무엇을 믿을지
 * 고르게 된다 — 고르게 만드는 것 자체가 이 제품이 없애려는 혼동이다.
 *
 * 아래 한계를 정한 것은 잴 때 어쩔 수 없이 섞이는 잡음이다. `currentTime` 은 렌더 양자
 * 단위로 올라가고(48kHz 에서 128 샘플이면 2.7밀리초), 오디오 시계와 동기 시계를 읽는 두
 * 줄 사이에 프레임 하나가 끼며, 동기 시계 자체도 추정값이다. 150은 그 잡음보다 한 자릿수
 * 위라 잡음만으로는 걸리지 않는다.
 *
 * **대가가 기울어 있어서 빡빡한 쪽을 골랐다.** 오탐의 대가는 재예약이고, 재예약은 이미
 * 지난 표지를 버릴 뿐 종점 시각을 건드리지 않는다 — 잃는 것은 박 한둘이지 소리가 아니다.
 * 미탐의 대가는 틀린 시각에 대한 확신, 곧 그 사람의 rally 하나다.
 *
 * **오탐으로 소리가 자주 죽거든 조항을 빼지 말고 이 숫자를 올린다** (2026-08-07 판정).
 * 빼면 틀린 시각에 대한 확신이 돌아오고, 그것은 침묵보다 나쁘다.
 */
export const SIGNAL_DRIFT_TOLERANCE_SEC = 0.15

/**
 * 예약이 아직 정직한가. **원인을 세지 않고 결과를 잰다.**
 *
 * 「지금 다시 계산하면 startAtSec 이 얼마여야 하는가」와 실제로 걸린 값의 차 하나를 본다.
 * 오디오 시계가 멈췄든 시계 동기가 다시 수렴했든 아직 생각하지 못한 셋째가 왔든, 예약이
 * 틀린 순간에 울리게 되었다면 그 사실이 이 한 값에 나타난다.
 */
export function checkSignalDrift(
  binding: SignalBinding,
  now: { audioNowSec: number; serverNowMs: number },
  toleranceSec = SIGNAL_DRIFT_TOLERANCE_SEC,
): SignalDriftVerdict {
  const latenessSec = binding.startAtSec - honestStartAtSec(binding.targetOpenMs, now)
  // 부호를 가르지 않는다. 이르게 우는 소리도 사람을 틀린 순간에 누르게 만든다.
  return { kind: Math.abs(latenessSec) > toleranceSec ? 'drifted' : 'on-time', latenessSec }
}

/**
 * 지금 이 순간 예약한다면 개시를 오디오 시계의 어디에 걸어야 하는가.
 *
 * **이 한 줄이 두 벌이 되면 안 된다.** 어긋남을 재는 쪽과 다시 계산하는 쪽이 서로 다른
 * 식을 쓰면, 재예약한 값이 자기 검사기를 통과하지 못하면서 무한히 다시 예약한다.
 */
export function honestStartAtSec(
  targetOpenMs: number,
  now: { audioNowSec: number; serverNowMs: number },
): number {
  return now.audioNowSec + (targetOpenMs - now.serverNowMs) / 1000
}

export type SignalRefresh =
  | { kind: 'keep'; latenessSec: number }
  | { kind: 'locked'; latenessSec: number; reason: string }
  | { kind: 'reschedule'; latenessSec: number; startAtSec: number }

/**
 * 어긋난 예약을 어떻게 할 것인가. **판단만 하고 노드는 건드리지 않는다.**
 */
export function decideSignalRefresh(input: {
  binding: SignalBinding
  audioNowSec: number
  serverNowMs: number
  audioState: string
  toleranceSec?: number
}): SignalRefresh {
  const now = { audioNowSec: input.audioNowSec, serverNowMs: input.serverNowMs }
  const { kind, latenessSec } = checkSignalDrift(input.binding, now, input.toleranceSec)

  // 멀쩡한 예약은 손대지 않는다. 재예약은 이미 지난 표지를 버리므로, 화면이 돌아올
  // 때마다 다시 걸면 아무 문제 없던 사람이 그 대가로 박을 잃는다.
  if (kind === 'on-time') return { kind: 'keep', latenessSec }

  // **잠긴 오디오에는 다시 예약하지 않는다.** `currentTime` 이 멈춰 있으므로 새 예약도
  // 멈춘 시계 위에 걸리고, 다음 검사가 또 어긋남을 보고 또 예약한다. 그 되풀이 동안
  // 소리는 나지 않는데 화면에는 아무 문제도 보이지 않는다. 잠김을 푸는 것은 재예약이
  // 아니라 사람의 제스처이므로(armAudio), 여기서 할 일은 그 사실을 말하고 멈추는 것뿐이다.
  if (input.audioState !== 'running') {
    return { kind: 'locked', latenessSec, reason: describeBlockedState(input.audioState) }
  }

  // 다시 계산한다. **개시가 이미 지났는지는 여기서 판정하지 않는다** — 그 규칙
  // (「늦었으면 울리지 않는다」)은 planSignal 하나가 지고 있고, 여기서 한 번 더 적으면
  // 판정식이 두 벌이 된다. 이 값을 scheduleSignal 에 넘기면 그쪽이 too-late 를 낸다.
  return {
    kind: 'reschedule',
    latenessSec,
    startAtSec: honestStartAtSec(input.binding.targetOpenMs, now),
  }
}

/**
 * 오디오를 무장한다. **사람의 제스처 안에서 불러야 한다** — 그 밖에서 부르면 브라우저가
 * 잠긴 상태를 풀어 주지 않는다. 세션 승계 재접속에서도 다시 한 번 필요하다.
 */
export async function armAudio(
  ctx: AudioContextLike,
): Promise<{ armed: boolean; reason: string }> {
  if (ctx.state === 'running') return { armed: true, reason: '' }
  try {
    await ctx.resume()
  } catch {
    // 깨우지 못한 까닭(브라우저의 오류 문자열)은 사람이 할 일을 바꾸지 않는다. 지금 상태의 이유를 말한다.
    return { armed: false, reason: describeBlockedState(ctx.state) }
  }
  const armed = ctx.state === 'running'
  return { armed, reason: armed ? '' : describeBlockedState(ctx.state) }
}
